/**
 * Hit testing and bounds.
 *
 * Components are tested against their declared `Bounds:` rectangle, which is
 * what the original stores them with and uses for picking — it is deliberately
 * the symbol's body, not the extent of its pin legs and labels.
 */

import type {
  ArcEntity, CadDocument, ComponentEntity, Entity, FillEntity,
  LineEntity, PadEntity, SpiralEntity, TextEntity,
} from '@sircad/core/model.ts';

export interface Rect {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * The document-space rectangle a component occupies.
 *
 * `bounds` is origin-relative (the parser rebases it on load), and `x`/`y` is
 * where that origin sits, so placement is a plain translation. Rotation about
 * the origin is reduced to the axis-aligned envelope, which is all picking
 * needs — the original picks on this rectangle too, not on the ink.
 */
export function componentRect(c: ComponentEntity): Rect {
  let x1 = Math.min(c.bounds.x1, c.bounds.x2);
  let x2 = Math.max(c.bounds.x1, c.bounds.x2);
  let y1 = Math.min(c.bounds.y1, c.bounds.y2);
  let y2 = Math.max(c.bounds.y1, c.bounds.y2);

  if (c.mirrored) {
    [x1, x2] = [-x2, -x1];
  }

  const rot = (((c.rotation ?? 0) % 360) + 360) % 360;
  // A quarter turn swaps the axes, but the two quarter turns do not swap them
  // the same way: 90° sends (x,y) to (-y,x) and 270° sends it to (y,-x). Both
  // used to take the 90° envelope, which put a part turned three quarters on
  // the wrong side of its own origin — so its pins could not be clicked.
  if (rot === 90) {
    [x1, y1, x2, y2] = [-y2, x1, -y1, x2];
  } else if (rot === 270) {
    [x1, y1, x2, y2] = [y1, -x2, y2, -x1];
  } else if (rot === 180) {
    [x1, y1, x2, y2] = [-x2, -y2, -x1, -y1];
  }

  return { x1: c.x + x1, y1: c.y + y1, x2: c.x + x2, y2: c.y + y2 };
}

export function entityBounds(e: Entity): Rect | null {
  switch (e.kind) {
    case 'line': {
      const l = e as LineEntity;
      const r = l.width / 2;
      return {
        x1: Math.min(l.x1, l.x2) - r, y1: Math.min(l.y1, l.y2) - r,
        x2: Math.max(l.x1, l.x2) + r, y2: Math.max(l.y1, l.y2) + r,
      };
    }
    case 'arc': {
      const a = e as ArcEntity;
      const r = a.radius + a.width / 2;
      return { x1: a.cx - r, y1: a.cy - r, x2: a.cx + r, y2: a.cy + r };
    }
    case 'pad': {
      const p = e as PadEntity;
      return {
        x1: p.x - p.sizeX / 2, y1: p.y - p.sizeY / 2,
        x2: p.x + p.sizeX / 2, y2: p.y + p.sizeY / 2,
      };
    }
    case 'spiral': {
      // Without this a spiral had no extent at all, so it could not be picked,
      // could not be caught by a rubber band, and could not be put in a block.
      const s = e as SpiralEntity;
      const r = Math.max(s.r1, s.r2) + s.width / 2;
      return { x1: s.cx - r, y1: s.cy - r, x2: s.cx + r, y2: s.cy + r };
    }
    case 'text': {
      const t = e as TextEntity;
      // Approximate: real extents need the font, and picking does not need it.
      // Which side of the insertion it lies on is not approximate, though — a
      // mirrored string runs back the other way, and a box on the wrong side
      // cannot be clicked at all.
      const w = (t.length ?? t.text.length * t.height * 0.7) * (t.mirrored ? -1 : 1);
      return {
        x1: Math.min(t.x, t.x + w), y1: t.y,
        x2: Math.max(t.x, t.x + w), y2: t.y + t.height,
      };
    }
    case 'fill': {
      const f = e as FillEntity;
      if (f.vertices.length === 0) return { ...f.bounds };
      let x1 = Infinity, y1 = Infinity, x2 = -Infinity, y2 = -Infinity;
      for (const v of f.vertices) {
        x1 = Math.min(x1, v.x); x2 = Math.max(x2, v.x);
        y1 = Math.min(y1, v.y); y2 = Math.max(y2, v.y);
      }
      return { x1, y1, x2, y2 };
    }
    case 'component':
      return componentRect(e as ComponentEntity);
    default:
      return null;
  }
}

/** Distance from a point to a line segment. */
function distToSegment(
  px: number, py: number, x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** Whether a point lands on one entity, ignoring layer visibility. */
function hits(e: Entity, x: number, y: number, tolerance: number): boolean {
  switch (e.kind) {
    case 'line': {
      const half = Math.max(e.width / 2, tolerance);
      return distToSegment(x, y, e.x1, e.y1, e.x2, e.y2) <= half;
    }
    case 'arc': {
      const d = Math.hypot(x - e.cx, y - e.cy);
      return Math.abs(d - e.radius) <= Math.max(e.width / 2, tolerance);
    }
    case 'pad': {
      const dx = Math.abs(x - e.x);
      const dy = Math.abs(y - e.y);
      return dx <= e.sizeX / 2 + tolerance && dy <= e.sizeY / 2 + tolerance;
    }
    default: {
      const b = entityBounds(e);
      return !!b && x >= b.x1 - tolerance && x <= b.x2 + tolerance
        && y >= b.y1 - tolerance && y <= b.y2 + tolerance;
    }
  }
}

/**
 * Topmost entity at a point.
 *
 * `tolerance` widens the pick to roughly a grid step so thin traces remain
 * clickable without pixel-perfect aim. Hidden and locked layers are skipped.
 *
 * `preferLayer` picks from the layer being worked on first, and only looks
 * wider if nothing there is under the cursor. Copper stacks up — a track on the
 * bottom, a track on the top and a pad can all sit on the same spot — so
 * without it a grab lands on whatever happens to be last in the file rather
 * than the thing being worked on. The fallback matters as much as the
 * preference: an item on another layer is still reachable when the active layer
 * is empty at that point, so nothing becomes unselectable.
 */
/**
 * How much of the drawing the pointer is allowed to touch.
 *
 * `visible` is the ordinary rule: what you cannot see, you cannot pick. `active`
 * narrows it to the working layer alone, which is how you edit a track running
 * under a plane pour without the pour answering every click.
 */
export type PickScope = 'visible' | 'active';

/**
 * The scope in force, and the layer it means by "active".
 *
 * Held here rather than passed in because it is one setting for the whole
 * program — the same answer for a click, a grab, a vertex drag and a list
 * dialog — and threading it through ten call sites and three tool modules
 * would give ten chances to forget it. The editor sets it when the preference
 * or the working layer changes; everything that picks reads it without asking.
 */
let currentScope: PickScope = 'visible';
let currentLayer = -1;

export function setPickScope(scope: PickScope, activeLayer: number): void {
  currentScope = scope;
  currentLayer = activeLayer;
}

export function pickScope(): PickScope {
  return currentScope;
}

/**
 * May this entity be selected, moved, edited or deleted?
 *
 * One rule, in one place. It used to be written out wherever something needed
 * to know — the click test had a copy, the "list them all" dialog had another
 * — which is how a rule ends up half-applied: picking honoured hidden layers
 * and the list beside it did not have to.
 *
 * Locked is included because the question is really "may I touch this", and a
 * locked layer's whole purpose is to be looked at and not touched.
 */
export function selectable(
  doc: CadDocument, e: Entity,
  scope: PickScope = currentScope, activeLayer = currentLayer,
): boolean {
  const layer = doc.layers[e.layer];
  if (layer && (!layer.visible || layer.locked)) return false;
  if (scope === 'active' && activeLayer >= 0 && e.layer !== activeLayer) return false;
  return true;
}

/**
 * A document-space point expressed in a component's own frame.
 *
 * The inverse of the placement the parser applies: translate, then undo the
 * rotation, then the mirror. Moving the click into the part is one transform;
 * moving every child out of it would be one per pin.
 */
function pointInComponent(c: ComponentEntity, x: number, y: number): { x: number; y: number } {
  const dx = x - c.x;
  const dy = y - c.y;
  const r = (-(c.rotation ?? 0) * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  const rx = dx * cos - dy * sin;
  const ry = dx * sin + dy * cos;
  return { x: c.mirrored ? -rx : rx, y: ry };
}

/**
 * The child of a placed part that a point lands on, or null.
 *
 * Only consulted when the pick is restricted to a kind — Edit Pad, Grab Line —
 * and the part itself is not that kind. An unrestricted click still picks the
 * whole part, which is what the original does and what moving one needs; but a
 * command that says "pad" and stops at the part edge can only ever reach the
 * loose vias, and on a real board those are a small minority of the pads.
 */
function hitChild(
  doc: CadDocument, c: ComponentEntity, x: number, y: number, tolerance: number,
  accept: (e: Entity) => boolean, scope: PickScope,
): { entity: Entity; exact: boolean } | null {
  const p = pointInComponent(c, x, y);
  let best: { entity: Entity; exact: boolean } | null = null;
  let bestSize = Infinity;

  // Ranked, not first-found. A grid step of tolerance is wider than a DIP's
  // tenth-inch pitch, so several pins of the same part answer every click; the
  // one the pointer is actually on has to win, or Edit Pad on an eight-pin
  // package always opens the same pin.
  for (let i = c.children.length - 1; i >= 0; i--) {
    const child = c.children[i]!;
    if (child.kind === 'component') {
      const inner = hitChild(doc, child, p.x, p.y, tolerance, accept, scope);
      if (inner && (!best || (inner.exact && !best.exact))) {
        best = inner;
        bestSize = pickSize(inner.entity);
      }
      continue;
    }
    if (!accept(child)) continue;
    if (!selectable(doc, child, scope, currentLayer)) continue;
    // Tested in the part's own frame, which is why the answer travels back with
    // the child: the caller holds document coordinates and cannot re-ask.
    const exact = hits(child, p.x, p.y, 0);
    if (!exact && !hits(child, p.x, p.y, tolerance)) continue;
    const size = pickSize(child);
    if (best && (best.exact ? 1 : 0) > (exact ? 1 : 0)) continue;
    if (best && (best.exact ? 1 : 0) === (exact ? 1 : 0) && size >= bestSize) continue;
    best = { entity: child, exact };
    bestSize = size;
  }
  return best;
}

/**
 * How big a thing is, for the tie between two equally good hits.
 *
 * The original ranks by `linelength` and keeps the *shorter* (`fcline`,
 * OGxyz12.cpp:232). It is a better rule than it looks: where a short stub meets
 * a long bus run, the stub is what you were pointing at — the long one is
 * merely passing through — and the same holds for the small pad against the
 * large one and the label against the box it sits in.
 */
function pickSize(e: Entity): number {
  if (e.kind === 'line') return Math.hypot(e.x2 - e.x1, e.y2 - e.y1);
  const b = entityBounds(e);
  if (!b) return Infinity;
  return Math.abs(b.x2 - b.x1) + Math.abs(b.y2 - b.y1);
}

/**
 * What lies under a point, ranked the way the original ranks it.
 *
 * `fcline` and its siblings (OGxyz12.cpp) do not take the first thing they
 * touch. They score every candidate and keep the best, and the score is three
 * preferences in order of importance:
 *
 *   +8  on the working layer
 *   +4  a free element rather than part of a component
 *   +2  squarely on it rather than merely near it
 *
 * and, between two of equal score, the **smaller** one wins.
 *
 * The middle bit is the one worth naming, because it is doing quiet work: it is
 * why clicking a wire that crosses a symbol gives you the wire. The symbol's
 * body is a rectangle enclosing the click and would otherwise win on every
 * schematic. This used to be handled by running an exact-hit pass before a
 * near-hit pass, which got the same answer in that one case and the wrong
 * answer whenever the part was on the working layer and the wire was not.
 *
 * The bottom bit stands in for the original's endpoint-versus-body distinction:
 * `fzline` scores a hit on the run of a line above a hit that only reaches its
 * tip, which is the same idea as being on a thing rather than near it. A grid
 * step of tolerance is wider than a pad, so without it a row of pins at a tenth
 * of an inch is a row of overlapping targets.
 */
export function hitTest(
  doc: CadDocument, x: number, y: number, tolerance: number,
  preferLayer?: number,
  accept?: (e: Entity) => boolean,
  scope: PickScope = currentScope,
): Entity | null {
  const pickable = (e: Entity): boolean => {
    if (!selectable(doc, e, scope, currentLayer)) return false;
    // A grab restricted to one kind (Grab Line, Grab Pad …) sees only that kind,
    // so a track can be picked out from under the part sitting on top of it.
    return !accept || accept(e);
  };

  let best: Entity | null = null;
  let bestScore = -1;
  let bestSize = Infinity;

  /** Weigh one candidate against the best so far. `exact` is already decided. */
  const consider = (e: Entity, free: boolean, exact: boolean): void => {
    const score = (preferLayer !== undefined && e.layer === preferLayer ? 8 : 0)
      + (free ? 4 : 0)
      + (exact ? 2 : 0);
    if (score < bestScore) return;
    const size = pickSize(e);
    if (score === bestScore && size >= bestSize) return;
    best = e;
    bestScore = score;
    bestSize = size;
  };

  for (let i = doc.entities.length - 1; i >= 0; i--) {
    const e = doc.entities[i]!;
    if (pickable(e)) {
      // A component is picked on its bounds rectangle, which encloses rather
      // than touches, so "squarely on it" would be true of every click inside a
      // part and the bit would say nothing. `fccomp` draws no such distinction
      // either.
      const exact = e.kind !== 'component' && hits(e, x, y, 0);
      if (exact || hits(e, x, y, tolerance)) consider(e, true, exact);
    }
    // A kind-restricted pick looks inside any part it lands on. The part's own
    // rectangle is tested first, because a part whose body covers the point can
    // still hold the pad that was actually clicked. What it finds is scored as
    // component geometry, so a free wire of the same kind still beats it.
    if (accept && e.kind === 'component' && hits(e, x, y, tolerance)) {
      const child = hitChild(doc, e, x, y, tolerance, accept, scope);
      if (child) consider(child.entity, false, child.exact);
    }
  }

  return best;
}
